import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import {
  GroupContext,
  MAX_GROUP_NAME_ERROR_MESSAGE,
  MAX_GROUP_NAME_LENGTH,
  OPPORTUNITY_REVIEW_BOARD_TABLE,
  PreferenceResponseStatus,
  UNKNOWN_ERROR_MESSAGE,
} from '@utils/userPreferencesUtils/Types';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { useOpportunityReviewBoardGroupEventState } from '../utils/OpportunityReviewBoardGroupContext';
import { useOpportunityReviewGroupState } from '@utils/hook/userPreferences/UseGroupState';
import { useAnalytics } from '@ecosystem/newron-client-lib';
import { useSelector } from 'react-redux';
import {
  groupElementClickedTrackingFunction,
  OPPORTUNITY_REVIEW_TABLE_ELEMENT_CLICKED_TITLE,
  TableElementClickedParam,
} from '@utils/page-analytics/model/GroupElementClickTracking';
import { PageName, PageType } from '@utils/pageConfig/Types';
import CreateGroupModal from '@components/create-group-modal/CreateGroupModal';

export interface Props {
  context: GroupContext;
  sortOrder: number;
}

export const CreateOpportunityReviewBoardModalGroup: React.FC<Props> = ({ context, sortOrder }) => {
  const pagesConfig = usePageConfig();
  const [groupName, setGroupName] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { createOpportunityReviewGroup } = useOpportunityReviewGroupState(context);
  const {
    selectedSegments,
    clearSelectedSegments,
    isCreateModalGroupPopUp,
    setIsCreateModalGroupPopUp,
    currentSelectedFeature,
  } = useOpportunityReviewBoardGroupEventState();
  const { track } = useAnalytics();
  const sideFiltersState = useSelector((state: any) => state.sideFilters);

  const onCancel = () => {
    setIsSaving(false);
    setError('');
    setGroupName('');
    setIsCreateModalGroupPopUp(false);
  };
  const constructDataBody = (isCateogry: boolean) => {
    if (isCateogry) {
      return {
        productGroup: selectedSegments,
        showPerformanceWithin: currentSelectedFeature.feature,
        sortOrder: sortOrder,
      };
    }
    return {
      value: selectedSegments,
      showPerformanceWithin: currentSelectedFeature.feature,
      sortOrder: sortOrder,
    };
  };
  const constructOpportunityReviewGroupPreference = (trimGroupName: string) => {
    createOpportunityReviewGroup.mutate(
      {
        key: trimGroupName,
        data: constructDataBody(context.isCategory),
      },
      {
        onSuccess: (response) => {
          setIsSaving(false);
          if (response.status === PreferenceResponseStatus.SUCCESS) {
            setGroupName('');
            clearSelectedSegments();
            onCancel();
          } else {
            setError(response.message);
          }
        },
        onError: () => {
          setError(UNKNOWN_ERROR_MESSAGE);
          setIsSaving(false);
        },
      },
    );
  };

  //Function to create group
  const onSaveGroup = (e: FormEvent) => {
    e.preventDefault();

    //Amplitude Tracking when Create Group button is click
    const trackingParam: TableElementClickedParam = {
      eventTitle: OPPORTUNITY_REVIEW_TABLE_ELEMENT_CLICKED_TITLE,
      elementName: `Create ${currentSelectedFeature.feature} Group`,
      elementType: `Table ${currentSelectedFeature.feature} Group`,
      track: track,
      optionChosen: selectedSegments.flatMap((selectedPg) => selectedPg).join(', '),
      pageName: PageName.OPPORTUNITY_REVIEW,
      pageType: PageType.OPPORTUNITY_REVIEW,
      sideFilters: sideFiltersState.appliedFilters,
      access: pagesConfig.access,
      tableName: OPPORTUNITY_REVIEW_BOARD_TABLE,
    };

    groupElementClickedTrackingFunction(trackingParam);

    const nameTrimmed = (groupName ?? '').trim();

    if (!nameTrimmed || nameTrimmed.length >= MAX_GROUP_NAME_LENGTH) {
      setError(MAX_GROUP_NAME_ERROR_MESSAGE);
      return;
    }

    setIsSaving(true);
    setError('');

    constructOpportunityReviewGroupPreference(nameTrimmed);
  };

  useEffect(() => {
    if (isCreateModalGroupPopUp && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isCreateModalGroupPopUp]);

  return (
    <div>
      <CreateGroupModal
        isCreateModalGroupPopUp={isCreateModalGroupPopUp}
        createMessage="Create Group"
        onCancel={onCancel}
        isSaving={isSaving}
        onSaveGroup={onSaveGroup}
        title={'Create Opportunity Review Group'}
        setGroupName={setGroupName}
        error={error}
      />
    </div>
  );
};
